import {
  Box,
  Button,
  Card,
  Heading,
  HStack,
  NativeSelect,
  Skeleton,
  Stack,
} from '@chakra-ui/react'

import { FiPlusSquare } from 'react-icons/fi'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { useAuth } from '../contexts/AuthContext'
import { useEventTypes } from '../contexts/EventTypesContext'
import { fetchEventById, fetchEvents } from '../services/firebaseService'
import { DEFAULT_TYPE } from '../constants/eventTypes'

import { toaster } from '../components/ui/toaster'
import { PageHeader } from '../components/ui/PageHeader'
import { useTranslation } from '../i18n'

import CreateEventForm from '../components/CreateEventForm'
import EventListItem from '../components/EventListItem'
import CreateWeddingDialog from '../components/CreateWeddingDialog'

const toMillis = (v) => v?.toMillis?.() ?? (v instanceof Date ? v.getTime() : 0)

function sortEvents(list, sortBy) {
  const out = [...list]
  if (sortBy === 'created') out.sort((a, b) => toMillis(b.createdAt) - toMillis(a.createdAt))
  else out.sort((a, b) => (a.name || '').localeCompare(b.name || ''))
  return out
}

export default function EventsPage() {
  const { isSysAdmin, eventRoles } = useAuth()
  const { typesList } = useEventTypes()
  const { t, lang } = useTranslation()
  const [events, setEvents] = useState([])
  const [loading, setLoading] = useState(true)
  const [sortBy, setSortBy] = useState('created')
  const [selectedTypes, setSelectedTypes] = useState([]) // empty = show every type
  const [createOpen, setCreateOpen] = useState(false)
  const [createType, setCreateType] = useState(null) // null | typeId

  const loadEvents = useCallback(async () => {
    setLoading(true)
    try {
      if (isSysAdmin) {
        setEvents(await fetchEvents({ isSysAdmin, userEvents: null, sortBy }))
        return
      }
      // Non-sysadmins only know their own assigned ids. Read those docs directly
      // and sort client-side: a collection query has to order on `name`/`createdAt`,
      // which silently drops any event missing that field.
      const docs = await Promise.all(eventRoles.map((r) => fetchEventById(r.eventid)))
      setEvents(sortEvents(docs.filter(Boolean), sortBy))
    } catch (err) {
      console.log(err)
      toaster.create({ title: t('events.failedToLoad'), description: err.message, status: 'error' })
    } finally {
      setLoading(false)
    }
  }, [isSysAdmin, eventRoles, sortBy, t])

  useEffect(() => {
    loadEvents()
  }, [loadEvents])

  // How many loaded events carry each type. Events created before types existed
  // have no `eventType`; getTypeConfig treats those as DEFAULT_TYPE, so do we.
  const typeCounts = useMemo(() => {
    const counts = new Map()
    for (const e of events) {
      const id = e.eventType || DEFAULT_TYPE
      counts.set(id, (counts.get(id) || 0) + 1)
    }
    return counts
  }, [events])

  // Only offer a pill for a type that actually has events, so no filter can
  // ever empty the list. Types missing from the registry keep their raw id.
  const filterTypes = useMemo(() => {
    const known = typesList.filter((cfg) => typeCounts.has(cfg.id))
    const unknown = [...typeCounts.keys()]
      .filter((id) => !typesList.some((cfg) => cfg.id === id))
      .map((id) => ({ id, label: { en: id }, tagColor: 'gray' }))
    return [...known, ...unknown]
  }, [typesList, typeCounts])

  // Drop selections whose type disappeared from the reloaded list.
  useEffect(() => {
    setSelectedTypes((prev) => {
      const next = prev.filter((id) => typeCounts.has(id))
      return next.length === prev.length ? prev : next
    })
  }, [typeCounts])

  const visibleEvents = useMemo(() => (
    selectedTypes.length === 0
      ? events
      : events.filter((e) => selectedTypes.includes(e.eventType || DEFAULT_TYPE))
  ), [events, selectedTypes])

  const toggleType = (id) => {
    setSelectedTypes((prev) => (
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    ))
  }

  const allActive = selectedTypes.length === 0

  return (<>
    <Stack gap={6}>
      <PageHeader title={t('events.title')} subtitle={t('events.subtitle')}>
        {filterTypes.length > 1 && (
          <HStack wrap="wrap" gap={2}>
            <Button
              size="xs"
              borderRadius="full"
              variant={allActive ? 'solid' : 'outline'}
              onClick={() => setSelectedTypes([])}
            >
              {t('events.filterAllTypes')} ({events.length})
            </Button>
            {filterTypes.map((cfg) => (
              <Button
                key={cfg.id}
                size="xs"
                borderRadius="full"
                colorPalette={cfg.tagColor}
                variant={selectedTypes.includes(cfg.id) ? 'solid' : 'outline'}
                onClick={() => toggleType(cfg.id)}
              >
                {cfg.label?.[lang] || cfg.label?.en || cfg.id} ({typeCounts.get(cfg.id)})
              </Button>
            ))}
          </HStack>
        )}
        <NativeSelect.Root size="sm" width="240px">
          <NativeSelect.Field placeholder={t('events.sortByPlaceholder')}
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value)}
            bg="var(--mc-bg-raised)"
            borderColor="var(--mc-border)"
            color="var(--mc-text)"
          >
            <option value="name">{t('events.sortByName')}</option>
            <option value="created">{t('events.sortByCreated')}</option>
          </NativeSelect.Field>
          <NativeSelect.Indicator />
        </NativeSelect.Root>
        {isSysAdmin && (
          <Button
            colorPalette={createOpen ? 'neutral' : 'brand'}
            variant={createOpen ? 'outline' : 'solid'}
            onClick={() => { setCreateOpen((p) => !p); setCreateType(null); }}
          >
            {!createOpen && <FiPlusSquare />}
            {createOpen ? t('events.closeCreator') : t('events.createEvent')}
          </Button>
        )}
      </PageHeader>

      {loading ? (
        <Stack gap={4}>
          {[1, 2, 3].map(i => (
            <Skeleton key={i} height="120px" borderRadius="md" />
          ))}
        </Stack>
      ) : (
        <Stack gap={4}>
          {createOpen && !createType && (
            <Card.Root bg="var(--mc-bg-surface)" border="1px solid" borderColor="var(--mc-border)">
              <Card.Body>
                <Heading size="sm" mb={3}>{t('events.whatKind')}</Heading>
                <HStack wrap="wrap">
                  {typesList.map((tcfg) => (
                    <Button
                      key={tcfg.id}
                      variant="outline"
                      colorPalette={tcfg.tagColor}
                      onClick={() => setCreateType(tcfg.id)}
                    >
                      {tcfg.label?.[lang] || tcfg.label?.en || tcfg.id}
                    </Button>
                  ))}
                </HStack>
              </Card.Body>
            </Card.Root>
          )}
          {createOpen && createType && createType !== 'wedding' && (
            <CreateEventForm
              eventType={createType}
              onDone={() => { setCreateOpen(false); setCreateType(null); loadEvents(); }}
            />
          )}
          <CreateWeddingDialog
            open={createOpen && createType === 'wedding'}
            onClose={() => { setCreateType(null); setCreateOpen(false); }}
            onCreated={() => { setCreateOpen(false); setCreateType(null); loadEvents(); }}
          />
          {visibleEvents.map((event) => (
            <EventListItem key={event.id} eventId={event.id} eventName={event.name} />
          ))}
        </Stack>

      )}
    </Stack></>
  )
}
